import { formatShortDate, normalize } from './dates';
import { isActiveMember } from './membership';
import { compareNames } from './members';
import {
  type Lookup,
  availabilityAt,
  blackoutOn,
  isEnabledIn,
  participationCounts,
  sexAllowed,
} from './schedule';
import type { Member, ScheduleEvent, SwapRequest, SwapStatus } from './types';

/**
 * "Pedir troca": quem está escalado numa escala publicada pede a um colega habilitado na
 * mesma função que assuma o seu lugar. O colega aceita ou recusa; aceitando, o líder da
 * função aprova (e a escala muda: o colega entra no lugar, já confirmado) ou recusa. Até
 * a aprovação, quem pediu continua escalado. Vale para qualquer perfil escalado — o
 * Administrador e os líderes também servem.
 *
 * O pedido em aberto é cancelado sozinho quando a escala muda por baixo dele (quem pediu
 * saiu da função, ou o colega entrou no evento): `cancelStaleSwaps`, chamado pelo
 * `syncTracking` de toda edição. Os encerrados ficam no evento para avisar quem pediu.
 */

export const SWAP_STATUS_LABEL: Record<SwapStatus, string> = {
  peer: 'aguardando resposta do colega',
  leader: 'aguardando aprovação do líder',
  approved: 'troca aprovada',
  rejected: 'troca recusada pelo líder',
  declined: 'o colega recusou',
  canceled: 'troca cancelada',
};

/** Pedido ainda em andamento (o colega ou o líder ainda vão responder). */
export function isOpenSwap(swap: Pick<SwapRequest, 'status'>): boolean {
  return swap.status === 'peer' || swap.status === 'leader';
}

/** O pedido em aberto de quem pediu, no evento. */
export function openSwapOf(event: ScheduleEvent, memberId: string): SwapRequest | null {
  return event.swaps.find((s) => s.fromId === memberId && isOpenSwap(s)) ?? null;
}

function scheduledRole(event: ScheduleEvent, memberId: string): string | null {
  return Object.entries(event.slots).find(([, ids]) => ids.includes(memberId))?.[0] ?? null;
}

function inEvent(event: ScheduleEvent, memberId: string): boolean {
  return Object.values(event.slots).some((ids) => ids.includes(memberId));
}

/**
 * Pode pedir troca: escalado numa escala publicada que ainda vai acontecer, sem check-in
 * e sem outro pedido em aberto no evento.
 */
export function canRequestSwap(event: ScheduleEvent, memberId: string, today: string): boolean {
  return (
    event.published &&
    event.date >= today &&
    !event.attendance[memberId] &&
    inEvent(event, memberId) &&
    !openSwapOf(event, memberId)
  );
}

export type SwapCandidateState = 'available' | 'unset' | 'unavailable' | 'blackout';

export interface SwapCandidate {
  member: Member;
  state: SwapCandidateState;
  /** Escalas no mês do evento: só ordena a lista (quem serviu menos primeiro), não é mostrado. */
  monthCount: number;
  /** Indisponível ou ausente: aparece, mas não pode ser escolhido. */
  blocked: boolean;
}

export const SWAP_CANDIDATE_LABEL: Record<SwapCandidateState, string> = {
  available: 'Disponível',
  unset: 'Disponibilidade não configurada',
  unavailable: 'Indisponível neste horário',
  blackout: 'Ausente nesta data',
};

/**
 * "Disponível", "Ausente nesta data"… — só a situação na data. Quantas vezes o colega
 * serviu no mês não aparece para quem pede a troca (é a distribuição da equipe, coisa dos
 * líderes).
 */
export function swapCandidateText(candidate: Pick<SwapCandidate, 'state'>): string {
  return SWAP_CANDIDATE_LABEL[candidate.state];
}

/**
 * Colegas para a troca: ativos, habilitados na função, do sexo que ela permite e fora do
 * evento — qualquer que seja o perfil de acesso. Primeiro quem pode (os disponíveis antes,
 * depois quem tem menos escalas no mês); indisponíveis e ausentes vêm por último,
 * bloqueados. `query` filtra pelo nome.
 */
export function swapCandidates(
  events: readonly ScheduleEvent[],
  eventId: string,
  roleId: string,
  requesterId: string,
  lookup: Lookup,
  query = ''
): SwapCandidate[] {
  const event = events.find((e) => e.id === eventId);
  const entry = lookup.roles.get(roleId);

  if (!event || !entry) return [];

  const counts = participationCounts(events, event.date.slice(0, 7));
  const q = normalize(query.trim());

  return [...lookup.members.values()]
    .filter(
      (m) =>
        m.id !== requesterId &&
        isActiveMember(m) &&
        isEnabledIn(m, entry.ministry.id, roleId) &&
        sexAllowed(entry.role, m) &&
        !inEvent(event, m.id) &&
        (!q || normalize(m.name).includes(q))
    )
    .map((member): SwapCandidate => {
      const availability = availabilityAt(member, event.date, event.time);
      const state: SwapCandidateState = blackoutOn(member, event.date) ? 'blackout' : availability;

      return {
        member,
        state,
        monthCount: counts.get(member.id) ?? 0,
        blocked: state === 'blackout' || state === 'unavailable',
      };
    })
    .sort(
      (a, b) =>
        Number(a.blocked) - Number(b.blocked) ||
        Number(a.state !== 'available') - Number(b.state !== 'available') ||
        a.monthCount - b.monthCount ||
        compareNames(a.member.name, b.member.name)
    );
}

/** Por que o pedido não pode ser feito (ou `null`). As mesmas regras na tela e no servidor. */
export function swapRequestError(
  events: readonly ScheduleEvent[],
  eventId: string,
  requesterId: string,
  toId: string,
  lookup: Lookup,
  today: string
): string | null {
  const event = events.find((e) => e.id === eventId);

  if (!event || !canRequestSwap(event, requesterId, today)) {
    return 'Só dá para pedir troca numa escala publicada, futura, em que você está e sem outro pedido em aberto.';
  }

  const roleId = scheduledRole(event, requesterId)!;
  const candidate = swapCandidates(events, eventId, roleId, requesterId, lookup).find(
    (c) => c.member.id === toId
  );

  if (!candidate) return 'Escolha um colega ativo, habilitado na função e que não esteja no evento.';
  if (candidate.blocked) return `${candidate.member.name} não pode nesta data.`;

  return null;
}

/**
 * Por que o líder não pode aprovar agora (ou `null`): a escala mudou desde o pedido —
 * quem pediu saiu da função, o colega entrou no evento ou deixou de estar habilitado.
 */
export function swapApprovalError(event: ScheduleEvent, swap: SwapRequest, lookup: Lookup): string | null {
  const to = lookup.members.get(swap.toId);
  const entry = lookup.roles.get(swap.roleId);

  if (
    !(event.slots[swap.roleId] ?? []).includes(swap.fromId) ||
    inEvent(event, swap.toId) ||
    !to ||
    !entry ||
    !isActiveMember(to) ||
    !isEnabledIn(to, entry.ministry.id, swap.roleId)
  ) {
    return 'A escala mudou desde o pedido. A troca foi cancelada.';
  }

  return null;
}

/**
 * Cancela os pedidos em aberto que a escala tornou impossíveis: quem pediu não está mais na
 * função, ou o colega já está no evento. Os encerrados não mudam.
 */
export function cancelStaleSwaps(event: ScheduleEvent): ScheduleEvent {
  let changed = false;
  const swaps = event.swaps.map((s) => {
    if (!isOpenSwap(s)) return s;
    if ((event.slots[s.roleId] ?? []).includes(s.fromId) && !inEvent(event, s.toId)) return s;

    changed = true;

    return { ...s, status: 'canceled' as const };
  });

  return changed ? { ...event, swaps } : event;
}

/** Pedidos de troca para a pessoa responder: em escalas publicadas que ainda vão acontecer. */
export function incomingSwaps(
  events: readonly ScheduleEvent[],
  memberId: string,
  today: string
): { event: ScheduleEvent; swap: SwapRequest }[] {
  return events
    .filter((e) => e.published && e.date >= today)
    .flatMap((event) =>
      event.swaps.filter((s) => s.toId === memberId && s.status === 'peer').map((swap) => ({ event, swap }))
    )
    .sort((a, b) => (a.event.date + a.event.time).localeCompare(b.event.date + b.event.time));
}

/**
 * Trocas que esperam o líder, recortadas pelas funções que ele edita (`editRole`), em
 * escalas que ainda vão acontecer.
 */
export function swapsAwaitingLeader(
  events: readonly ScheduleEvent[],
  editRole: (roleId: string) => boolean,
  today: string
): { event: ScheduleEvent; swap: SwapRequest }[] {
  return events
    .filter((e) => e.date >= today)
    .flatMap((event) =>
      event.swaps.filter((s) => s.status === 'leader' && editRole(s.roleId)).map((swap) => ({ event, swap }))
    );
}

/** "Culto da Noite, dom 19 out às 19:00" — o evento no texto do pedido. */
export function swapEventLabel(event: Pick<ScheduleEvent, 'title' | 'date' | 'time'>): string {
  return `${event.title}, ${formatShortDate(event.date)} às ${event.time}`;
}
